// ./src/components/generic/StatusInput/StatusInput.tsx
//
// A small Active / Disabled toggle for an entity's lifecycle `status` (an int —
// see STATUSES from @sdflc/utils, never a bool). Reusable in any edit drawer.
// Anything that isn't DISABLED reads as Active.

import React from "react";
import { SegmentedControl, Input } from "@mantine/core";
import { STATUSES } from "@sdflc/utils";
import { useTranslation } from "react-i18next";

export interface StatusInputProps {
  /** Current status int (e.g. STATUSES.ACTIVE / STATUSES.DISABLED). */
  value: number | null | undefined;
  /** Called with the chosen status int. */
  onChange: (status: number) => void;
  label?: string;
  description?: string;
  disabled?: boolean;
  /**
   * ⚠ A server refusal keyed to `status`. Any drawer that lists `status` in its KNOWN_FORM_FIELDS needs a control
   * that can show it: `getFormErrors` puts a KNOWN name in `fields` and never in `genericErrors`, so a listed
   * field with nowhere to land SWALLOWS the message and the save fails silently (found 2026-09-19).
   */
  error?: string | null;
}

export const StatusInput: React.FC<StatusInputProps> = ({
  value,
  onChange,
  label,
  description,
  disabled = false,
  error,
}) => {
  const { t } = useTranslation(["common"]);

  const data = [
    {
      value: String(STATUSES.ACTIVE),
      label: t("common:status.active", { defaultValue: "Active" }),
    },
    {
      value: String(STATUSES.DISABLED),
      label: t("common:status.disabled", { defaultValue: "Disabled" }),
    },
  ];

  const current =
    value === STATUSES.DISABLED
      ? String(STATUSES.DISABLED)
      : String(STATUSES.ACTIVE);

  return (
    <Input.Wrapper
      label={label ?? t("common:status.label", { defaultValue: "Status" })}
      description={description}
      error={error || undefined}
    >
      <SegmentedControl
        data={data}
        value={current}
        onChange={(next) => onChange(Number(next))}
        disabled={disabled}
        fullWidth
        mt={4}
      />
    </Input.Wrapper>
  );
};
